import styles from '../../../pages/orders/orders-page.module.scss'
import { observer } from 'mobx-react-lite'
import {
  TableContainer,
  TableHeader,
  TableBodyContainer,
  TableBodyLine,
  TableBodyLineProps,
  PaginationButtons,
  PageContainer
} from '@touche-lts/panel-ui/dist'
import { useRootStore } from 'mobx-stores/store-provider'
import { useEffect, useRef } from 'react'
import { OrdersDTO } from 'models'
import { OrdersTableSortProps, OrdersTableStatus, orderTableCellWidth } from 'mobx-stores/utils/orders-table-base/orders-table-base.types'
import cl from 'classnames'
import { OrderDetails } from "pages/orders/order-details"
import { useGetScrollWidth } from 'hooks/custom-hooks'

const AccountOrdersTableComponent = () => { 

  const {
    ordersStore: {
      isColumnMarked,
      getSortDirection,
      handleSortDirectionChange,
      searchProps,
      onSearchPropsChange,
      searchResults,
      paginationInfo,
      onPageSelect,
      triggerSearch,
      statusShown,
      statusTableHeader
    },
    ordersDetailsStore: {
      setOrderDetails,
      isOrderDetailsVisible
    }
  } = useRootStore(),

  orderDetailsRef: any = useRef(),

  handleClickOutside = (event) => {
    if (orderDetailsRef.current && !orderDetailsRef.current.contains(event.target)) {
      setOrderDetails(false);
    }
  },

  
  handleOrderDetailsPopup = (orderId: number): void => {
    setOrderDetails(true, orderId)
  }
  
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
        document.removeEventListener('click', handleClickOutside, true);
    };
  })

  useEffect(() => {
    triggerSearch({ resetPageNumber: true })
  }, [triggerSearch])

  const { scrollX, handleScroll } = useGetScrollWidth()
  const spacebarSpacing = 4.8

  const getBodyLineCells = (
    order: OrdersDTO
  ): TableBodyLineProps['cells'] => {
    const cells: any = [
      {
        // widthPercentage: 100,
        children: (
          <>
            #{order.number}
          </>
        ),
        onClick: () => handleOrderDetailsPopup(order.id),
        isColumnMarked: isColumnMarked(OrdersTableSortProps.NUMBER),
        widthPx : orderTableCellWidth.NUMBER
      },
      {
        // widthPercentage: 100,
        children: (
          <>
            {new Date(order.date).toLocaleDateString()} {new Date(order.date).toLocaleTimeString()}
          </>
        ),
        isColumnMarked: isColumnMarked(OrdersTableSortProps.DATE),
        widthPx : orderTableCellWidth.DATE
      },
      {
        // widthPercentage: 100,
        children: (
          <>
            <span className={styles[order.orderStatus]}></span> {statusShown(order.orderStatus)}
          </>
        ),
        isColumnMarked: isColumnMarked(OrdersTableSortProps.ORDERSTATUS),
        widthPx : orderTableCellWidth.ORDERSTATUS
      },
      {
        // widthPercentage: 100,
        children: (
          <>
            {order.price}
          </>
        ),
        isColumnMarked: isColumnMarked(OrdersTableSortProps.TOTAL),
        widthPx : orderTableCellWidth.TOTAL
      },
      {
        // widthPercentage: 100,
        children: (
          <>
            {order?.store?.name}
          </>
        ),
        isColumnMarked: isColumnMarked(OrdersTableSortProps.STORE),
        widthPx : orderTableCellWidth.STORE
      },
    ]

    return cells
  },

  tableColumns: any = [
    {
      label: 'ORDER NUMBER',
      // widthPx: productsTableWidths.sku,
      isColumnMarked: isColumnMarked(OrdersTableSortProps.NUMBER),
      sortDirection: getSortDirection(OrdersTableSortProps.NUMBER),
      setSortDirection: handleSortDirectionChange(OrdersTableSortProps.NUMBER),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.number,
        onChange: (number => onSearchPropsChange({ number }))
      },
      widthPx : orderTableCellWidth.NUMBER
    },
    {
      label: 'ORDER DATE',
      isColumnMarked: isColumnMarked(OrdersTableSortProps.DATE),
      sortDirection: getSortDirection(OrdersTableSortProps.DATE),
      setSortDirection: handleSortDirectionChange(
        OrdersTableSortProps.DATE
      ),
      periodFilterProps: {
        period: searchProps.orderDate,
        setPeriod: orderDate => onSearchPropsChange({ orderDate }),
        className: styles.periodFilter
      },
      widthPx : orderTableCellWidth.DATE
    },
    {
      label: 'STATUS',
      isColumnMarked: isColumnMarked(OrdersTableSortProps.ORDERSTATUS),
      sortDirection: getSortDirection(OrdersTableSortProps.ORDERSTATUS),
      setSortDirection: handleSortDirectionChange(
        OrdersTableSortProps.ORDERSTATUS
      ),
      selectProps: {
        noneLabel: 'All',
        options: statusTableHeader,
        value: searchProps.status,
        onChange: (status: OrdersTableStatus) => onSearchPropsChange({ status })
      },
      widthPx : orderTableCellWidth.ORDERSTATUS
    },
    {
        label: 'PURCHASE TOTAL',
        isColumnMarked: isColumnMarked(OrdersTableSortProps.TOTAL),
        sortDirection: getSortDirection(OrdersTableSortProps.TOTAL),
        setSortDirection: handleSortDirectionChange(
          OrdersTableSortProps.TOTAL
        ),
        sliderFilterProps: {
          label: 'order product',
          min: 0,
          max: 1000,
          value: [searchProps.minAmount, searchProps.maxAmount],
          onChange: ([minAmount, maxAmount]) =>
            onSearchPropsChange({ minAmount, maxAmount })
        },
        widthPx : orderTableCellWidth.TOTAL,
      },
      {
        label: 'STORE',
        isColumnMarked: isColumnMarked(OrdersTableSortProps.STORE),
        sortDirection: getSortDirection(OrdersTableSortProps.STORE),
        setSortDirection: handleSortDirectionChange(
          OrdersTableSortProps.STORE
        ),
        inputProps: {
          placeholder: 'Search...',
          value: searchProps.store,
          onChange: (store => onSearchPropsChange({ store }))
        },
        widthPx : orderTableCellWidth.STORE + spacebarSpacing
      }
  ]

  return (
    <PageContainer className={cl(styles.scrollable, 'container')} style={{marginTop: 80}}>
        <TableContainer colorPreset='dark' style={{ overflowX: 'auto'}} className={styles.thinScroll} onScroll={handleScroll}>
        <TableHeader colorPreset='dark' tableColumns={tableColumns} />
        <TableBodyContainer style={{minHeight: 400}} className={styles.tableBodyContainer}>
            {searchResults.map((order: OrdersDTO) => {
            return (
                <TableBodyLine colorPreset='dark' cells={getBodyLineCells(order)} />
            )
            })}
        </TableBodyContainer>
        <PaginationButtons
            colorPreset='dark'
            alwaysShow
            {...paginationInfo}
            onPageSelect={onPageSelect}
            style={ {width: scrollX}}
        />
        </TableContainer>
        <div ref={orderDetailsRef}>
          {isOrderDetailsVisible && (
            <OrderDetails />
          )}
        </div>      
    </PageContainer>
  )
},

AccountOrdersTable = observer(AccountOrdersTableComponent) //here
export { AccountOrdersTable }


